/**
 * Auth-v2 web refresh helper — wires the SDK's `refreshHandler`
 * config field to the browser's fetch API using the HttpOnly
 * refresh cookie for transport.
 *
 * Every web dashboard's `services/api.ts` calls
 * {@link createWebRefreshHandler} once and passes the result to the
 * SDK constructor. The SDK invokes it whenever an outgoing request
 * lands a `401` — the handler POSTs to `/auth/refresh`, updates the
 * in-memory access token via {@link setAccessToken}, and returns
 * the fresh access token so the SDK can retry the failed request.
 *
 * The refresh cookie itself is never touched by JS. Since the fetch
 * runs with `credentials: 'include'`, the browser attaches
 * `hero_rt` automatically; the backend's `Set-Cookie` on the
 * response rotates it.
 *
 * See `apps/backend/src/app/auth/README.md`.
 */
import { getAccessToken, setAccessToken, clearSessionTokens } from './tokens';
import { withCrossTabRefreshLock } from './refreshLock';

export type WebRefreshResponse = {
  access_token?: string;
};

export type WebRefreshHandlerOptions = {
  /** Defaults to `/auth/refresh`. Admin panel uses `/admin-auth/refresh`. */
  refreshPath?: string;
};

async function performWebRefresh(
  baseURL: string,
  refreshPath: string,
): Promise<string | null> {
  // Snapshot before the network round-trip. `/auth/switch-org` may
  // replace the access token while this refresh is in flight; applying
  // the refresh result afterward would mint the *pre-switch* org back
  // into memory and break party checks (viewer leg / price edits).
  const tokenAtStart = getAccessToken();
  try {
    const res = await fetch(`${baseURL}${refreshPath}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    });
    if (!res.ok) {
      // Definitive client/auth failures kill the session. 401/403 =
      // cookie rejected; 400 = unusable refresh request (Zod) that
      // will never recover on retry. Anything else (5xx, gateway
      // timeout, Cloud Run cold start) is transient: keep the access
      // token + `hero.hasSession` sentinel so the next 401-triggered
      // or boot-time refresh retries the still-valid cookie instead
      // of bouncing the user to the login screen.
      if (res.status === 400 || res.status === 401 || res.status === 403) {
        // Only clear if nothing newer landed while we were refreshing.
        if (getAccessToken() === tokenAtStart) {
          clearSessionTokens();
        }
      }
      return null;
    }
    const envelope = (await res.json()) as { data?: WebRefreshResponse };
    const nextAccessToken = envelope.data?.access_token;
    if (!nextAccessToken) {
      // 200 without a token is a server contract violation, not proof
      // the session is dead — don't wipe local state over it.
      return null;
    }

    const tokenNow = getAccessToken();
    if (tokenNow !== tokenAtStart && tokenNow !== null) {
      return tokenNow;
    }

    setAccessToken(nextAccessToken);
    return nextAccessToken;
  } catch {
    // Network failure (offline, laptop waking from sleep, DNS blip).
    // The refresh cookie is untouched and very likely still valid —
    // leave the session artifacts alone and let a later call retry.
    return null;
  }
}

export function createWebRefreshHandler(baseURL: string, options?: WebRefreshHandlerOptions) {
  const refreshPath = options?.refreshPath ?? '/auth/refresh';
  const coreHandler = () => performWebRefresh(baseURL, refreshPath);
  const locked = withCrossTabRefreshLock(coreHandler);
  return async (): Promise<string | null> => {
    const tokenAtStart = getAccessToken();
    const token = await locked();
    // Follower tabs receive a peer token without going through
    // `performWebRefresh` — persist it unless a newer local token
    // (e.g. org switch) already replaced what we started with.
    if (token && getAccessToken() !== token) {
      const tokenNow = getAccessToken();
      if (tokenNow === tokenAtStart || tokenNow === null) {
        setAccessToken(token);
      }
    }
    return getAccessToken() ?? token;
  };
}

/** Admin-panel refresh — separate cookie (`hero_admin_rt`) and endpoint. */
export function createAdminWebRefreshHandler(baseURL: string) {
  return createWebRefreshHandler(baseURL, { refreshPath: '/admin-auth/refresh' });
}
